// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { isNonEmptyString } from "@insite/client-framework/Common/Utilities/StringUtilities";
import { HasParentProductId, withParentProductId } from "@insite/client-framework/Components/ParentProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import {
    getSelectedProductPath,
    getSession,
    getSettingsCollection,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import {
    getProductState,
    getVariantChildrenDataView,
} from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductDetailsProductCard from "@tps/App/Components/ProductDetails/ProductDetailsProductCard";
import { CustomAppState } from "@tps/App/Store/Reducers";
import { MyBranchWarehouse } from "@tps/XDL/types";
import { xScreen } from "@tps/XDL/Utilities";
import color from "@tps/XDL/Utilities/color";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & HasParentProductId;

const mapStateToProps = (state: CustomAppState, ownProps: HasParentProductId) => {
    const session = getSession(state);
    const productState = getProductState(state, ownProps.parentProductId);
    const location = getLocation(state);

    const productPath =
        getSelectedProductPath(state) ||
        (location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname);
    return {
        variantTraits: productState.value?.variantTraits,
        variantChildren: getVariantChildrenDataView(state, ownProps.parentProductId).value,
        productInfosById: state.pages.productDetails.productInfosById,
        hideItemsOutOfStock: (getSettingsCollection(state).catalogCustomSettings as any).hideItemsOutOfStock,
        isAuthenticated: session.isAuthenticated && !session.isGuest,
        defaultWarehouse: session.properties?.myBranch
            ? (JSON.parse(session.properties.myBranch) as MyBranchWarehouse)
            : null,
        isProductLoading: state.xProductDetails.isProductLoading?.[productPath || ""],
        isParentSpecialOrder: productState.value?.detail?.isSpecialOrder,
    };
};

export interface ProductDetailsVariantOptionsStyles {
    wrapper?: InjectableCss;
    variantTraitSection?: InjectableCss;
    variantTraitValueSection?: InjectableCss;
    topSection?: InjectableCss;
    bottomSection?: InjectableCss;
    descriptionDocumnetsCountryWrapper?: InjectableCss;
    descriptionDocumnetsWrapper?: InjectableCss;
    qtyAndPriceWrapper?: InjectableCss;
    description?: TypographyProps;
    country?: TypographyProps;
    documents?: InjectableCss;
    documentLink?: LinkPresentationProps;
    inventoryMessage?: TypographyProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    inquireMessageText?: TypographyProps;
}

export const variantOptionsListStyles: ProductDetailsVariantOptionsStyles = {
    wrapper: {
        css: css`
            width: 100%;
            border: 1px solid ${color("common.border")};
        `,
    },
    variantTraitValueSection: {
        css: css`
            padding: 12px;
            &:not(:last-child) {
                border-bottom: 1px solid ${color("common.border")};
            }
        `,
    },
    topSection: {
        css: css`
            display: flex;
            justify-content: space-between;
            ${xScreen("lte", "md")} {
                flex-direction: column;
                justify-content: flex-start;
                row-gap: 4px;
            }
        `,
    },
    bottomSection: {
        css: css`
            display: flex;
            justify-content: space-between;
            ${xScreen("lte", "md")} {
                flex-direction: column;
                justify-content: flex-start;
                row-gap: 16px;
            }
        `,
    },
    country: {
        size: 12,
        css: css`
            line-height: 17px;
        `,
    },
    description: {
        variant: "h5",
        css: css`
            margin-bottom: 0;
            margin-right: 12px;
        `,
    },
    descriptionDocumnetsWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
        `,
    },
    documents: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
        `,
    },
    documentLink: {
        css: css`
            line-height: 17px;
        `,
        typographyProps: {
            weight: "bold",
            size: 12,
            css: css`
                line-height: 17px;
            `,
        },
    },
    inventoryMessage: {
        size: 12,
        lineHeight: "17px",
    },
};

const styles = variantOptionsListStyles;

const ProductDetailsVariantOptions: React.FC<Props> = ({
    variantChildren,
    variantTraits,
    productInfosById,
    hideItemsOutOfStock,
    isAuthenticated,
    defaultWarehouse,
    isProductLoading,
    isParentSpecialOrder,
}) => {
    const shouldFilter = isAuthenticated && hideItemsOutOfStock;
    const isLoading = isProductLoading;

    const filteredVariantChildren = React.useMemo(() => {
        if (!variantChildren || !productInfosById) {
            return [];
        }
        return variantChildren.filter(product => {
            const productInfo = productInfosById[product.id];
            const qtyOnHand = productInfo?.inventory?.qtyOnHand ?? 0;
            const unitNetPrice = productInfo?.pricing?.unitNetPrice ?? 0;
            return qtyOnHand > 0 && unitNetPrice > 0;
        });
    }, [variantChildren, productInfosById]);

    const finalVariantChildren = shouldFilter ? filteredVariantChildren : variantChildren;

    const inquireMessage =
        defaultWarehouse && isNonEmptyString(defaultWarehouse?.Phone) && isNonEmptyString(defaultWarehouse?.ContactName)
            ? siteMessage(
                  "Inquire_Products_Message_WarehouseInfo",
                  defaultWarehouse.ContactName,
                  defaultWarehouse.Phone,
                  defaultWarehouse.Phone,
              )
            : siteMessage("Inquire_Products_Message");

    if (isLoading && shouldFilter) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    }

    if (
        (finalVariantChildren?.length === 0 && !isLoading && shouldFilter) ||
        (isParentSpecialOrder && isAuthenticated)
    ) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <Typography {...styles.inquireMessageText} data-test-selector="productDetails_inquireProducts">
                    {inquireMessage}
                </Typography>
            </StyledWrapper>
        );
    }

    if (!variantTraits || variantTraits.length === 0 || !finalVariantChildren) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            {finalVariantChildren?.map((product: ProductModel) => (
                <StyledWrapper {...styles.variantTraitValueSection} key={`${product.id}`}>
                    <ProductDetailsProductCard
                        product={product}
                        productInfo={productInfosById && productInfosById[product.id]}
                        inquireMessage={inquireMessage}
                    />
                </StyledWrapper>
            ))}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withParentProductId(connect(mapStateToProps)(ProductDetailsVariantOptions)),
    definition: {
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
